import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { type GridCellParams, type GridColDef, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'
import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { useGetPromoCodes } from '@dhl/mdp-api'

import { renderDataTableDate } from '~/ui-lib/DataTable/dataTableUtil'
import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField } from '~/common'
import { convertToPromoCode, type PromoCode } from '~/modules/promocodes/model'

export const PromoCodesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
  const { data: promoCodeDtos = [], isLoading } = useGetPromoCodes()
  const [search, setSearch] = useState('')

  const allPromoCodes = useMemo(() => promoCodeDtos.map(convertToPromoCode), [promoCodeDtos])

  const promoCodes = useMemo(
    () => (search ? allPromoCodes.filter(({ key = '' }) => key.toLowerCase().includes(search)) : allPromoCodes),
    [allPromoCodes, search]
  )

  const handleSearchChange = useCallback((value: string) => {
    setPage(0)
    setSearch(value)
  }, [])

  const getRemainingValue = (value: never, row: PromoCode) => row?.maxUses - row?.currentUses

  const onClickEdit = (params: GridCellParams) => {
    const value = params.row
    navigate(links.editPromoCodeLink(value.key))
  }

  const handleRowClick = (value: PromoCode) => {
    navigate(links.promoCodeDetailsLink(value.key || ''))
  }

  const renderCellEdit = (params: GridCellParams) => {
    return (
      <IconButton aria-label="edit promocode" onClick={() => onClickEdit(params)}>
        <EditSolidIcon data-testid="edit-promocode" />
      </IconButton>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'key',
      headerName: t('admin.promoCodesPage.promoCodeOverview.code'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'validFrom',
      headerName: t('admin.promoCodesPage.promoCodeOverview.validFrom'),
      flex: 1,
      valueFormatter: renderDataTableDate,
    },
    {
      field: 'validUntil',
      headerName: t('admin.promoCodesPage.promoCodeOverview.validUntil'),
      flex: 1,
      valueFormatter: renderDataTableDate,
    },
    {
      field: 'currentUses',
      headerName: t('admin.promoCodesPage.promoCodeOverview.remaining'),
      flex: 1,
      valueGetter: getRemainingValue,
      type: 'number',
      sortable: true,
    },
    {
      field: 'maxUses',
      headerName: t('admin.promoCodesPage.promoCodeOverview.usageLimit'),
      flex: 1,
      type: 'number',
      sortable: true,
    },
    {
      field: 'actions',
      headerName: t('admin.promoCodesPage.actions'),
      sortable: false,
      flex: 1,
      disableReorder: true,
      disableExport: true,
      resizable: false,
      filterable: false,
      editable: false,
      renderCell: renderCellEdit,
    },
  ]

  return (
    <>
      <Box mb={3}>
        <Grid direction="row" container justifyContent="space-between">
          <Grid>
            <SearchTextField onChange={handleSearchChange} transform={lowercaseTransform} />
          </Grid>
          <Grid>
            <Button
              variant="contained"
              color="primary"
              data-testid="add-promocode"
              startIcon={<PlusIcon />}
              onClick={() => navigate(links.addPromoCodeLink())}
            >
              {t('admin.promoCodesPage.addButton')}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            getRowId={(row) => row.key}
            loading={isLoading}
            data-testid="promocodes-table"
            autoHeight
            rows={promoCodes}
            columns={columns}
            paginationModel={{
              page,
              pageSize,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'key', sort: 'asc' }],
              },
            }}
            onSortModelChange={() => {
              setPage(0)
            }}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={promoCodes.length}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      </Box>
    </>
  )
}
